import { Text } from '@/components/animated-type/text';
import { AnnotatedType } from '@/components/annotated-type';
import { StoryHeader, StoryTagline, StoryTheme, StoryTitle } from '@/components/microstory/header';
import { Outro } from '@/components/microstory/outro';
import { StoryQuestion, useStoryQuestion } from '@/components/microstory/question';
import { StoriesNavigation } from '@/components/microstory/stories-navigation';
import { Theme, Themes } from '@/components/microstory/theme';
import { SeoHead } from '@/components/seo-head';
import { FutureFortunesChapter1 } from '@/components/stories/future-fortunes/chapter-1';
import { SurveyBackground } from '@/components/survey-background';
import data from '@/data/future-fortunes.json';
import { countryIdToName } from '@/utils/country-id-to-name';
import { createGetServerSideProps } from '@/utils/create-get-server-side-props';
import { getStoryProps } from '@/utils/get-story-props';
import { useLayout } from '@/utils/use-layout';
import { t, Trans } from '@lingui/macro';
import { InferGetServerSidePropsType } from 'next';
import { useSharingImage } from '@/utils/use-sharing-image';
import { useLingui } from '@lingui/react';

export const getServerSideProps = createGetServerSideProps(
  async () => ({ data: data as FutureFortunesData }),
  getStoryProps('future-fortunes', 'global-outlook')
);

export default function FutureFortunes(
  props: InferGetServerSidePropsType<typeof getServerSideProps>
) {
  const { storySlug, baseUrl, data, sharingUrl, linkedStoryIds, relatedStoryId } = props;

  useLayout('white');
  useLingui();

  const answers = [
    {
      id: 'answer-1',
      label: t({ id: 'future-fortunes.question.answer-1', message: '30%' }),
      country: countryIdToName('ES'),
      inCountry: countryIdToName('ES', true),
    },
    {
      id: 'answer-2',
      label: t({ id: 'future-fortunes.question.answer-2', message: '40%' }),
      country: countryIdToName('US'),
      inCountry: countryIdToName('US', true),
    },
    {
      id: 'answer-3',
      label: t({ id: 'future-fortunes.question.answer-3', message: '50%' }),
      country: countryIdToName('CM'),
      inCountry: countryIdToName('CM', true),
    },
    {
      id: 'answer-4',
      label: t({ id: 'future-fortunes.question.answer-4', message: '60%' }),
      country: countryIdToName('UA'),
      inCountry: countryIdToName('UA', true),
    },
    {
      id: 'answer-5',
      label: t({ id: 'future-fortunes.question.answer-5', message: '70%' }),
      country: countryIdToName('ML'),
      inCountry: countryIdToName('ML', true),
    },
    {
      id: 'answer-6',
      label: t({ id: 'future-fortunes.question.answer-6', message: '80%' }),
      country: countryIdToName('ET'),
      inCountry: countryIdToName('ET', true),
    },
  ];

  const sharingImage = useSharingImage('future-fortunes');

  const pageTitle = t({ id: 'future-fortunes.title', message: 'Future fortunes' });
  const pageDescription = t({
    id: 'future-fortunes.description',
    message:
      'Most young people feel the world is getting better over time. How do they rate prospects in their own country as their generation prepares for adulthood?',
  });
  const { selectedAnswer, handleAnswer } = useStoryQuestion<(typeof answers)[number]>();

  return (
    <>
      <SeoHead
        baseUrl={baseUrl}
        pageTitle={pageTitle}
        pageDescription={pageDescription}
        sharingImage={sharingImage}
      />

      <Theme theme={Themes.sunny} hasBackButton>
        <StoryHeader backgroundId={storySlug}>
          <StoryTheme>
            <Trans id="future-fortunes.theme">Optimism</Trans>
          </StoryTheme>
          <StoryTitle>{pageTitle}</StoryTitle>
          <StoryTagline>
            <Trans id="future-fortunes.tagline">
              <Text type="heading">
                Most young people feel the world is getting better over time. How do they rate{' '}
                <span>economic prospects</span> in their own country as their generation prepares
                for adulthood?
              </Text>
            </Trans>
          </StoryTagline>
        </StoryHeader>

        <SurveyBackground />

        <StoryQuestion
          question={t({
            id: 'future-fortunes.question.question',
            message:
              "We asked young people if they think children in their country will be economically better or worse off than their parents when they're older.",
          })}
          answerHeading={t({
            id: 'future-fortunes.question.text',
            message: "What share do you think say they'll be better off?",
          })}
          answers={answers}
          onAnswer={handleAnswer}
          selectedAnswer={selectedAnswer}
          response={t({
            id: 'future-fortunes.question.response',
            message: `That's true ${selectedAnswer?.inCountry}!`,
          })}
        />

        <FutureFortunesChapter1 data={data} />

        <Outro
          relatedStoryId={relatedStoryId}
          sharing={{
            title: t({ id: 'future-fortunes.sharing.title', message: 'Future fortunes' }),
            text: t({
              id: 'future-fortunes.sharing.text',
              message:
                'Most young people feel the world is getting better over time. How do they rate prospects in their own country as their generation prepares for adulthood?',
            }),
            url: sharingUrl,
          }}
        >
          <Trans id="future-fortunes.closing-question">
            How can we boost young people's <AnnotatedType>economic prospects</AnnotatedType> in an
            uncertain future?
          </Trans>
        </Outro>
      </Theme>

      <StoriesNavigation storyIds={linkedStoryIds} />
    </>
  );
}
